import { permissionsFor } from "@/lib/permissions";
import { billingForForm, invoicePreview } from "@/lib/billing";
import { useEffect, useState, useCallback, useMemo } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCustomization } from "@/context/CustomizationContext";
import { fmtCurrency, fmtCurrencyMasked, fmtDate, fmtMonth, fmtNum } from "@/lib/format";
import { getEffectiveWorkspaceColumns } from "@/lib/workspaceColumns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ExportButton from "@/components/common/ExportButton";
import PresetsMenu from "@/components/common/PresetsMenu";
import MultiSelectFilter from "@/components/common/MultiSelectFilter";
import ImportDialog from "@/components/common/ImportDialog";
import { toast } from "sonner";
import { Plus, Search, MoreHorizontal, Pencil, Trash2, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

const PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const RIGHT_TYPES = new Set(["number", "currency", "percentage"]);

const pricingModeLabel = (mode) => ({
  fte: "FTE",
  transactions: "Transactions",
  man_hours: "Man Hours",
  percentage: "Percentage",
}[mode] || "FTE");

const formatServicePricing = (service) => (
  service?.billing_mode === "percentage"
    ? `${fmtNum(service.percentage_rate ?? service.pricing, 2)}%`
    : fmtCurrency(service?.pricing, 2)
);

const EMPTY_FORM = {
  reporting_month: "", client_name: "", service_key: "", process: "", service: "", rev_code: "",
  pricing: "", billable_ftes: "", prorated_ftes: "", invoice_amount: "", override_invoice: false, remarks: "",
  custom: {},
};

export default function RevenueDetailsPage() {
  const { user } = useAuth();
  const canEdit = permissionsFor(user?.role).canEdit;
  const canDelete = permissionsFor(user?.role).canEdit;
  const canViewFinancial = permissionsFor(user?.role).canViewFinancial;
  const { getColumns, sectionLabels } = useCustomization();
  const sectionTitle = sectionLabels?.revenue_details || "Revenue Details";
  const summaryTitle = sectionLabels?.revenue_summary || "Revenue Summary";
  const [data, setData] = useState({ items: [], total: 0, totals: { invoice: 0, billable: 0, prorated: 0 } });
  const [meta, setMeta] = useState({ months: [], detail_clients: [], processes: [] });
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [monthFilter, setMonthFilter] = useState([]);
  const [clientFilter, setClientFilter] = useState([]);
  const [processFilter, setProcessFilter] = useState([]);
  const [sortBy, setSortBy] = useState("reporting_month");
  const [sortDir, setSortDir] = useState("desc");
  const [cellEdit, setCellEdit] = useState(null); // {item, col, value}
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // ---- Control Panel driven layout ----
  const configuredColumns = getColumns("revenue_details");
  const allColumns = useMemo(
    () => getEffectiveWorkspaceColumns("revenue_details", configuredColumns),
    [configuredColumns],
  );
  const columns = useMemo(() => allColumns.filter((c) => c.visible !== false), [allColumns]);
  const customCols = useMemo(() => allColumns.filter((c) => !c.core && c.visible !== false), [allColumns]);
  const colByKey = useMemo(() => Object.fromEntries(allColumns.map((c) => [c.key, c])), [allColumns]);
  const labelOf = (key) => colByKey[key]?.label || key;

  // ---- Multi-select helpers ----
  const allPageIds = useMemo(() => data.items.map((i) => i.id), [data.items]);
  const allSelected = allPageIds.length > 0 && allPageIds.every((id) => selected.has(id));
  const someSelected = !allSelected && allPageIds.some((id) => selected.has(id));
  const toggleRow = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allPageIds));

  const loadMeta = useCallback(() => {
    api.get("/meta").then((res) => setMeta(res.data)).catch(() => {});
    api.get("/services").then((res) => setServices(res.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir };
    if (search) params.search = search;
    if (monthFilter.length) params.month = monthFilter.join(",");
    if (clientFilter.length) params.client_name = clientFilter.join(",");
    if (processFilter.length) params.process = processFilter.join(",");
    api.get("/revenue-details", { params })
      .then((res) => setData(res.data))
      .catch(() => toast.error("Failed to load revenue details"))
      .finally(() => setLoading(false));
  }, [page, pageSize, search, monthFilter, clientFilter, processFilter, sortBy, sortDir]);

  useEffect(loadMeta, [loadMeta]);
  useEffect(load, [load]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); setSelected(new Set()); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));

  const currentFilters = useMemo(() => ({
    search: searchInput, months: monthFilter, clients: clientFilter, processes: processFilter,
    sort_by: sortBy, sort_dir: sortDir,
  }), [searchInput, monthFilter, clientFilter, processFilter, sortBy, sortDir]);

  const asArray = (v, legacy) => Array.isArray(v) ? v : (legacy && legacy !== "all" ? [legacy] : []);

  const applyPreset = useCallback((f) => {
    setSearchInput(f.search || "");
    setSearch(f.search || "");
    setMonthFilter(asArray(f.months, f.month));
    setClientFilter(asArray(f.clients, f.client));
    setProcessFilter(asArray(f.processes, f.process));
    if (f.sort_by) setSortBy(f.sort_by);
    if (f.sort_dir) setSortDir(f.sort_dir);
    setPage(1);
    setSelected(new Set());
  }, []);

  const toggleSort = (field) => {
    if (!field) return;
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
    setPage(1);
    setSelected(new Set());
  };

  const fmtCustom = (col, v) => {
    if (col.type === "currency") {
      if (col.financial && !canViewFinancial) return <span className="text-slate-400">$</span>;
      if (v == null) return <span className="text-slate-400">$</span>;
      return fmtCurrency(v, col.decimals ?? 2);
    }
    if (col.type === "percentage") {
      if (col.financial && !canViewFinancial) return <span className="text-slate-400">%</span>;
      if (v == null) return <span className="text-slate-400">%</span>;
      return `${fmtNum(v, col.decimals ?? 2)}%`;
    }
    if (v == null || v === "") return "-";
    if (col.type === "number") return fmtNum(v, col.decimals ?? 2);
    if (col.type === "date") return fmtDate(v);
    if (col.type === "boolean") return v ? "Yes" : "No";
    return String(v);
  };

  const openCellEdit = (item, col) => {
    if (!canEdit) return;
    if (col.financial && !canViewFinancial) return; // financial fields restricted
    const raw = col.core ? item[col.key] : item.custom?.[col.key];
    let value = raw ?? "";
    if (col.key === "reporting_month") value = (raw || "").slice(0, 7);
    else if (col.type === "boolean") value = raw == null ? "" : String(!!raw);
    setCellEdit({ item, col, value });
  };

  const saveCell = async () => {
    const { item, col, value } = cellEdit;
    const field = col.key;
    if (field === "reporting_month" && !value) { toast.error(`${col.label} is required.`); return; }
    if ((field === "client_name" || field === "service") && !String(value).trim()) {
      toast.error(`${col.label} is required.`);
      return;
    }
    const isNum = RIGHT_TYPES.has(col.type);
    let newVal = isNum ? (parseFloat(value) || 0) : value;
    if (!col.core) {
      if (isNum) newVal = value === "" ? null : (parseFloat(value) || 0);
      else if (col.type === "boolean") newVal = value === "" ? null : value === "true";
    }
    // If the current invoice matches the workspace billing rule, keep it
    // auto-calculated. This also resolves legacy percentage rows without a
    // stored snapshot through their current catalog record.
    const serviceForItem = services.find((service) => (
      String(service.process || "").trim().toLowerCase() === String(item.process || "").trim().toLowerCase()
      && String(service.service || "").trim().toLowerCase() === String(item.service || "").trim().toLowerCase()
    ));
    const itemBasis = billingForForm(item, serviceForItem, item, allColumns);
    const preview = invoicePreview({ ...item, billing_snapshot: itemBasis });
    const wasAuto = preview != null
      && Math.abs((item.invoice_amount || 0) - preview) < 0.01;
    const payload = {
      reporting_month: field === "reporting_month" ? value : (item.reporting_month || "").slice(0, 7),
      client_name: field === "client_name" ? String(value).trim() : item.client_name,
      process: field === "process" ? newVal : (item.process || ""),
      service: field === "service" ? String(value).trim() : item.service,
      rev_code: field === "rev_code" ? newVal : (item.rev_code || ""),
      pricing: field === "pricing" ? newVal : (item.pricing || 0),
      billable_ftes: field === "billable_ftes" ? newVal : (item.billable_ftes || 0),
      prorated_ftes: field === "prorated_ftes" ? newVal : (item.prorated_ftes || 0),
      invoice_amount: field === "invoice_amount" ? newVal : (wasAuto ? null : (item.invoice_amount ?? 0)),
      remarks: field === "remarks" ? newVal : (item.remarks || ""),
      custom: col.core ? (item.custom || {}) : { ...(item.custom || {}), [field]: newVal },
    };
    setSaving(true);
    try {
      const res = await api.put(`/revenue-details/${item.id}`, payload);
      const c = res.data?.cascade;
      if (c && (c.revenue_details || c.engagements)) {
        toast.success(`Client renamed everywhere — ${c.revenue_details} revenue line(s) and ${c.engagements} engagement(s) updated`);
      } else {
        toast.success(`${col.label} updated`);
      }
      setCellEdit(null);
      load();
      loadMeta();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    const latestMonth = meta.months?.[0] || "";
    setForm({ ...EMPTY_FORM, reporting_month: latestMonth ? latestMonth.slice(0, 7) : "", custom: {} });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    const service = services.find((candidate) => (
      String(candidate.process || "").trim().toLowerCase() === String(item.process || "").trim().toLowerCase()
      && String(candidate.service || "").trim().toLowerCase() === String(item.service || "").trim().toLowerCase()
    ));
    setEditing(item);
    setForm({
      reporting_month: item.reporting_month?.slice(0, 7) || "",
      client_name: item.client_name || "",
      service_key: service?.id || "",
      process: item.process || "",
      service: item.service || "",
      rev_code: item.rev_code || "",
      pricing: item.pricing ?? "",
      billable_ftes: item.billable_ftes ?? "",
      prorated_ftes: item.prorated_ftes ?? "",
      invoice_amount: item.invoice_amount ?? "",
      override_invoice: true,
      remarks: item.remarks || "",
      custom: { ...(item.custom || {}) },
    });
    setDialogOpen(true);
  };

  const onServiceSelect = (svcId) => {
    const svc = services.find((s) => s.id === svcId);
    if (svc) {
      setForm((f) => ({
        ...f, service_key: svcId, service: svc.service, process: svc.process,
        rev_code: svc.rev_code, pricing: svc.pricing,
        billing_mode: svc.billing_mode || "fte",
        percentage_rate: svc.percentage_rate ?? svc.pricing ?? "",
      }));
    }
  };

  const setCustomField = (key, value) => setForm((f) => ({ ...f, custom: { ...f.custom, [key]: value } }));

  const selectedService = services.find((service) => service.id === form.service_key);
  const billingBasis = billingForForm(form, selectedService, editing, allColumns);
  const computedInvoice = useMemo(() => {
    return invoicePreview({ ...form, billing_snapshot: billingBasis });
  }, [billingBasis, form]);

  const save = async () => {
    if (!form.reporting_month || !form.client_name || !form.service) {
      toast.error("Month, client name, and service are required.");
      return;
    }
    setSaving(true);
    const custom = {};
    customCols.forEach((c) => {
      let v = form.custom?.[c.key];
      if (v === "" || v == null) { custom[c.key] = null; return; }
      if (RIGHT_TYPES.has(c.type)) v = parseFloat(v) || 0;
      else if (c.type === "boolean") v = v === true || v === "true";
      custom[c.key] = v;
    });
    const payload = {
      reporting_month: form.reporting_month,
      client_name: form.client_name.trim(),
      process: form.process,
      service: form.service,
      rev_code: form.rev_code,
      pricing: canViewFinancial ? (parseFloat(form.pricing) || 0) : 0,
      billable_ftes: parseFloat(form.billable_ftes) || 0,
      prorated_ftes: parseFloat(form.prorated_ftes) || 0,
      invoice_amount: canViewFinancial && form.override_invoice && form.invoice_amount !== "" && form.invoice_amount != null
        ? parseFloat(form.invoice_amount) : null,
      remarks: form.remarks,
      custom,
    };
    try {
      if (editing) {
        const res = await api.put(`/revenue-details/${editing.id}`, payload);
        const c = res.data?.cascade;
        if (c && (c.revenue_details || c.engagements)) {
          toast.success(`Revenue line updated — client renamed across ${c.revenue_details} revenue line(s) and ${c.engagements} engagement(s)`);
        } else {
          toast.success("Revenue line updated");
        }
      } else {
        await api.post("/revenue-details", payload);
        toast.success("Revenue line added");
      }
      setDialogOpen(false);
      load();
      loadMeta();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/revenue-details/${deleting.id}`);
      toast.success("Revenue line deleted");
      setDeleting(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const confirmBulkDelete = async () => {
    const ids = [...selected];
    try {
      await Promise.all(ids.map((id) => api.delete(`/revenue-details/${id}`)));
      toast.success(`${ids.length} line${ids.length !== 1 ? "s" : ""} deleted`);
      setSelected(new Set());
      setBulkDeleting(false);
      load();
    } catch {
      toast.error("Some deletions failed — please retry");
    }
  };

  // ---- Cell renderers (Control Panel drives order/visibility/labels) ----
  const renderCell = (item, col) => {
    const key = col.key;
    const restricted = col.financial && !canViewFinancial;
    const editableTd = canEdit && !restricted ? "cursor-pointer hover:bg-teal-50/70" : "";
    const tdTitle = editableTd ? "Click to edit this value" : undefined;
    if (!col.core) {
      return (
        <td key={key} className={`px-3 py-2 max-w-[160px] truncate ${RIGHT_TYPES.has(col.type) ? "text-right tabular-nums" : ""} ${editableTd}`}
          title={tdTitle} onClick={() => openCellEdit(item, col)} data-testid={`rd-cell-${key}-${item.id}`}>
          {fmtCustom(col, item.custom?.[key])}
        </td>
      );
    }
    switch (key) {
      case "reporting_month":
        return (
          <td key={key} className={`px-3 py-2 whitespace-nowrap font-medium text-slate-800 ${editableTd}`} title={tdTitle}
            onClick={() => openCellEdit(item, col)} data-testid={`rd-cell-month-${item.id}`}>{fmtMonth(item.reporting_month)}</td>
        );
      case "client_name":
        return (
          <td key={key} className={`px-3 py-2 max-w-[220px] truncate ${editableTd}`} title={item.client_name}
            onClick={() => openCellEdit(item, col)} data-testid={`rd-cell-client-${item.id}`}>{item.client_name}</td>
        );
      case "service":
        return (
          <td key={key} className={`px-3 py-2 max-w-[180px] truncate ${editableTd}`} title={item.service}
            onClick={() => openCellEdit(item, col)}>{item.service}</td>
        );
      case "process":
        return (
          <td key={key} className={`px-3 py-2 max-w-[170px] truncate text-slate-500 ${editableTd}`} title={item.process}
            onClick={() => openCellEdit(item, col)}>{item.process}</td>
        );
      case "rev_code":
        return (
          <td key={key} className={`px-3 py-2 text-slate-500 font-mono ${editableTd}`} title={tdTitle}
            onClick={() => openCellEdit(item, col)}>{item.rev_code}</td>
        );
      case "pricing":
        return (
          <td key={key} className={`px-3 py-2 text-right tabular-nums ${editableTd}`} title={tdTitle}
            onClick={() => openCellEdit(item, col)} data-testid={`rd-cell-pricing-${item.id}`}>
             {!canViewFinancial
               ? <span className="text-slate-400">{services.find((service) => (
                 String(service.process || "").trim().toLowerCase() === String(item.process || "").trim().toLowerCase()
                 && String(service.service || "").trim().toLowerCase() === String(item.service || "").trim().toLowerCase()
               ))?.billing_mode === "percentage" ? "%" : "$"}</span>
               : item.pricing == null
               ? <span className="text-slate-400">{item.billing_snapshot?.mode === "percentage" ? "%" : "$"}</span>
               : item.billing_snapshot?.mode === "percentage"
                 ? `${fmtNum(item.pricing, 2)}%`
                 : fmtCurrency(item.pricing, col.decimals ?? 2)}
          </td>
        );
      case "billable_ftes":
      case "prorated_ftes":
        return (
          <td key={key} className={`px-3 py-2 text-right tabular-nums ${editableTd}`} title={tdTitle}
            onClick={() => openCellEdit(item, col)}>{fmtNum(item[key], col.decimals ?? 2)}</td>
        );
      case "invoice_amount":
        return (
          <td key={key} className={`px-3 py-2 text-right tabular-nums font-semibold ${editableTd}`} title={tdTitle}
            onClick={() => openCellEdit(item, col)} data-testid={`rd-cell-invoice-${item.id}`}>
             {!canViewFinancial
               ? <span className="text-slate-400 font-normal">$</span>
               : item.invoice_amount == null ? <span className="text-slate-400 font-normal">$</span> : fmtCurrency(item.invoice_amount, col.decimals ?? 2)}
          </td>
        );
      case "remarks":
        return (
          <td key={key} className={`px-3 py-2 max-w-[150px] truncate text-slate-400 ${editableTd}`} title={item.remarks || tdTitle}
            onClick={() => openCellEdit(item, col)}>{item.remarks || "-"}</td>
        );
      default:
        return (
          <td key={key} className={`px-3 py-2 ${editableTd}`} title={tdTitle}
            onClick={() => openCellEdit(item, col)}>{item[key] ?? "-"}</td>
        );
    }
  };

  const cellInput = () => {
    const { col, value } = cellEdit;
    if (col.key === "reporting_month") {
      return (
        <Input autoFocus type="month" data-testid="rd-cell-edit-input" value={value}
          onChange={(e) => setCellEdit({ ...cellEdit, value: e.target.value })}
          onKeyDown={(e) => e.key === "Enter" && saveCell()} />
      );
    }
    if (col.type === "dropdown") {
      return (
        <Select value={String(value || "")} onValueChange={(v) => setCellEdit({ ...cellEdit, value: v })}>
          <SelectTrigger data-testid="rd-cell-edit-select"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>
            {(col.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    if (col.type === "boolean") {
      return (
        <Select value={String(value)} onValueChange={(v) => setCellEdit({ ...cellEdit, value: v })}>
          <SelectTrigger data-testid="rd-cell-edit-select"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="true">Yes</SelectItem>
            <SelectItem value="false">No</SelectItem>
          </SelectContent>
        </Select>
      );
    }
    return (
      <Input
        autoFocus
        data-testid="rd-cell-edit-input"
        type={RIGHT_TYPES.has(col.type) ? "number" : col.type === "date" ? "date" : "text"}
        step={RIGHT_TYPES.has(col.type) ? "0.01" : undefined}
        value={cellEdit.value}
        onChange={(e) => setCellEdit({ ...cellEdit, value: e.target.value })}
        onKeyDown={(e) => e.key === "Enter" && saveCell()}
      />
    );
  };

  return (
    <div className="space-y-4" data-testid="revenue-details-page">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="revenue-details-search-input" placeholder="Search client, service, remarks..."
            className="pl-9 rounded-lg" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
        </div>
        <MultiSelectFilter label="Month" testId="revenue-details-month-filter" className="w-[150px]"
          options={meta.months.map((m) => ({ value: m, label: fmtMonth(m) }))}
          selected={monthFilter} onChange={(v) => { setMonthFilter(v); setPage(1); setSelected(new Set()); }} />
        <MultiSelectFilter label="Client" testId="revenue-details-client-filter" className="w-[190px]"
          options={meta.detail_clients}
          selected={clientFilter} onChange={(v) => { setClientFilter(v); setPage(1); setSelected(new Set()); }} />
        <MultiSelectFilter label="Process" testId="revenue-details-process-filter" className="w-[180px]"
          options={meta.processes}
          selected={processFilter} onChange={(v) => { setProcessFilter(v); setPage(1); setSelected(new Set()); }} />
        {canDelete && selected.size > 0 && (
          <Button variant="destructive" size="sm" className="rounded-lg"
            data-testid="revenue-details-bulk-delete-button"
            onClick={() => setBulkDeleting(true)}>
            <Trash2 className="h-4 w-4 mr-1" /> Delete {selected.size} selected
          </Button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <PresetsMenu pageKey="revenue-details" currentFilters={currentFilters} onApply={applyPreset} />
          {canViewFinancial && (
            <ImportDialog entity="revenue-details" entityLabel={sectionTitle}
              testIdPrefix="revenue-details" onImported={() => { load(); loadMeta(); }} />
          )}
          {canViewFinancial && <ExportButton testId="revenue-details-export-button" />}
          {canEdit && (
            <Button data-testid="revenue-details-add-button" onClick={openAdd} size="sm" className="rounded-lg">
              <Plus className="h-4 w-4 mr-1" /> Add Line Item
            </Button>
          )}
        </div>
      </div>

      {/* Totals strip */}
      <div className="flex flex-wrap gap-3" data-testid="revenue-details-totals">
        <div className="rounded-lg border bg-white px-4 py-2 text-sm shadow-sm">
          <span className="text-slate-500">Invoice Total:</span>{" "}
          <span className="font-semibold tabular-nums" data-testid="rd-invoice-total">{fmtCurrencyMasked(data.totals.invoice)}</span>
        </div>
        <div className="rounded-lg border bg-white px-4 py-2 text-sm shadow-sm">
          <span className="text-slate-500">Billable FTEs:</span>{" "}
          <span className="font-semibold tabular-nums">{fmtNum(data.totals.billable)}</span>
        </div>
        <div className="rounded-lg border bg-white px-4 py-2 text-sm shadow-sm">
          <span className="text-slate-500">Prorated FTEs:</span>{" "}
          <span className="font-semibold tabular-nums">{fmtNum(data.totals.prorated)}</span>
        </div>
        <div className="rounded-lg border bg-white px-4 py-2 text-sm shadow-sm">
          <span className="text-slate-500">Line Items:</span>{" "}
          <span className="font-semibold tabular-nums">{data.total}</span>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border bg-white shadow-[var(--app-shadow-sm)] overflow-hidden">
        {loading ? (
          <div className="p-4 space-y-2">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : data.items.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-500" data-testid="revenue-details-empty">
            No revenue lines match your filters.
          </div>
        ) : (
          <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-21rem)]">
            <table className="sticky-table w-full text-xs" data-testid="revenue-details-table">
              <thead>
                <tr className="border-b">
                  {canDelete && (
                    <th className="px-2 py-1 border-b w-8">
                      <Checkbox
                        checked={allSelected ? true : someSelected ? "indeterminate" : false}
                        onCheckedChange={toggleAll}
                        aria-label="Select all"
                        data-testid="revenue-details-select-all"
                      />
                    </th>
                  )}
                  {columns.map((col) => {
                    const sortField = col.core ? col.key : `custom.${col.key}`;
                    const right = RIGHT_TYPES.has(col.type);
                    return (
                      <th key={col.key} className={`px-1 py-1 font-semibold text-slate-600 whitespace-nowrap border-b ${right ? "text-right" : "text-left"}`}>
                        <button
                          data-testid={`rd-sort-${sortField}`}
                          onClick={() => toggleSort(sortField)}
                          title={`Sort by ${col.label}`}
                          className={`inline-flex items-center gap-1 rounded px-2 py-1.5 hover:bg-slate-100 transition-[background-color] duration-150 ${right ? "flex-row-reverse" : ""} ${sortBy === sortField ? "text-[hsl(var(--primary))]" : ""}`}
                        >
                          {col.label}
                          {sortBy === sortField
                            ? (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)
                            : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                        </button>
                      </th>
                    );
                  })}
                  <th className="px-1 py-1 border-b"><span className="px-2" /></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item.id} className="border-b last:border-0 hover:bg-slate-50 transition-[background-color] duration-150" data-testid={`revenue-detail-row-${item.id}`}>
                    {canDelete && (
                      <td className="px-2 py-1.5">
                        <Checkbox
                          checked={selected.has(item.id)}
                          onCheckedChange={() => toggleRow(item.id)}
                          aria-label="Select row"
                          data-testid={`revenue-detail-select-${item.id}`}
                        />
                      </td>
                    )}
                    {columns.map((col) => renderCell(item, col))}
                    <td className="px-2 py-2">
                      {canEdit && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`revenue-detail-actions-${item.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(item)} className="cursor-pointer" data-testid={`revenue-detail-edit-${item.id}`}>
                            <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
                          </DropdownMenuItem>
                          {canDelete && (
                          <DropdownMenuItem onClick={() => setDeleting(item)} className="cursor-pointer text-red-600 focus:text-red-600" data-testid={`revenue-detail-delete-${item.id}`}>
                            <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                          </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && data.total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-2.5 text-xs text-slate-500">
            <span>Page {page} of {totalPages} · {data.total} lines</span>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <div className="flex items-center gap-1.5">
                <label htmlFor="revenue-details-page-size" className="whitespace-nowrap">Rows per page</label>
                <Select value={String(pageSize)} onValueChange={(value) => {
                  setPageSize(Number(value));
                  setPage(1);
                  setSelected(new Set());
                }}>
                  <SelectTrigger id="revenue-details-page-size" className="h-7 w-[76px] rounded-lg px-2" data-testid="revenue-details-page-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" size="sm" className="h-7 w-7 rounded-lg p-0" disabled={page <= 1}
                onClick={() => setPage(1)} data-testid="revenue-details-first-page" title="First page">
                <ChevronsLeft className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="sm" className="h-7 rounded-lg" disabled={page <= 1}
                onClick={() => setPage(page - 1)} data-testid="revenue-details-prev-page">
                <ChevronLeft className="h-3.5 w-3.5" /> Prev
              </Button>
              <Button variant="outline" size="sm" className="h-7 rounded-lg" disabled={page >= totalPages}
                onClick={() => setPage(page + 1)} data-testid="revenue-details-next-page">
                Next <ChevronRight className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="sm" className="h-7 w-7 rounded-lg p-0" disabled={page >= totalPages}
                onClick={() => setPage(totalPages)} data-testid="revenue-details-last-page" title="Last page">
                <ChevronsRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Revenue Line" : "Add Revenue Line"}</DialogTitle>
            <DialogDescription>
              {editing ? "Update this monthly revenue line item." : "Add a new monthly revenue line. Selecting a service auto-fills pricing."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{labelOf("reporting_month")} *</Label>
              <Input type="month" data-testid="detail-form-month" value={form.reporting_month}
                onChange={(e) => setForm({ ...form, reporting_month: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{labelOf("client_name")} *</Label>
              <Input data-testid="detail-form-client" list="client-options" placeholder="Client name"
                value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
              <datalist id="client-options">
                {meta.detail_clients.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>{labelOf("service")} *</Label>
              <Select value={form.service_key} onValueChange={onServiceSelect}>
                <SelectTrigger data-testid="detail-form-service">
                  <SelectValue placeholder={form.service || "Select a service from the catalog"} />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {services.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.process} · {s.service} · {s.service_code}{canViewFinancial ? ` · ${formatServicePricing(s)}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
               <p className="text-[11px] text-slate-400">Auto-fills process, rev code, pricing model &amp; pricing from the Legend.</p>
            </div>
            {canViewFinancial && (
            <div className="space-y-1.5">
               <Label>{labelOf("pricing")} ({billingBasis.mode === "percentage" ? "%" : "$"} · {pricingModeLabel(billingBasis.mode)})</Label>
               <Input
                 type="number"
                 min="0"
                 max={billingBasis.mode === "percentage" ? "100" : undefined}
                 step="0.01"
                 inputMode="decimal"
                 data-testid="detail-form-pricing"
                 className="text-right tabular-nums"
                value={form.pricing} onChange={(e) => setForm({ ...form, pricing: e.target.value })} />
            </div>
            )}
            <div className="space-y-1.5">
              <Label>{labelOf("rev_code")}</Label>
              <Input data-testid="detail-form-revcode" value={form.rev_code}
                onChange={(e) => setForm({ ...form, rev_code: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{labelOf("billable_ftes")}</Label>
              <Input type="number" step="0.01" data-testid="detail-form-billable" className="text-right tabular-nums"
                value={form.billable_ftes} onChange={(e) => setForm({ ...form, billable_ftes: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{labelOf("prorated_ftes")}</Label>
              <Input type="number" step="0.01" data-testid="detail-form-prorated" className="text-right tabular-nums"
                value={form.prorated_ftes} onChange={(e) => setForm({ ...form, prorated_ftes: e.target.value })} />
            </div>
            {canViewFinancial ? (
            <div className="col-span-2 rounded-lg bg-slate-50 border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500">
                  Auto-calculated invoice: <span className="font-semibold text-slate-800 tabular-nums">
                    {computedInvoice == null
                      ? `Enter ${colByKey[billingBasis.basis_field || billingBasis.collection_field]?.label || "the pricing basis value"}`
                      : fmtCurrency(computedInvoice)}
                  </span>
                  <span className="text-slate-400">
                    {billingBasis.mode === "percentage"
                      ? " (selected value × percentage)"
                      : billingBasis.mode === "fte"
                        ? ` (pricing × ${colByKey[billingBasis.basis_field]?.label || "FTE quantity"})`
                        : " (pricing × selected quantity)"}
                  </span>
                </p>
                <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                  <Checkbox checked={form.override_invoice} data-testid="detail-form-override-checkbox"
                    onCheckedChange={(c) => setForm({ ...form, override_invoice: !!c, invoice_amount: c ? (form.invoice_amount || computedInvoice) : "" })} />
                  Override
                </label>
              </div>
              {form.override_invoice && (
                <Input type="number" step="0.01" data-testid="detail-form-invoice" className="text-right tabular-nums"
                  placeholder="Invoice amount" value={form.invoice_amount}
                  onChange={(e) => setForm({ ...form, invoice_amount: e.target.value })} />
              )}
            </div>
            ) : (
            <div className="col-span-2 rounded-lg bg-slate-50 border p-3" data-testid="detail-form-invoice-masked">
              <p className="text-xs text-slate-500">
                {labelOf("invoice_amount")}: <span className="font-semibold text-slate-400">$</span>
                <span className="text-slate-400"> (calculated using the record’s billing method)</span>
              </p>
            </div>
            )}
            <div className="col-span-2 space-y-1.5">
              <Label>{labelOf("remarks")}</Label>
              <Textarea rows={2} data-testid="detail-form-remarks" placeholder="Optional notes"
                value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
            </div>

            {customCols.length > 0 && (
              <>
                <div className="col-span-2 border-t pt-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Custom Fields</p>
                </div>
                {customCols.map((c) => (
                  <div key={c.key} className="space-y-1.5">
                    <Label>{c.label}</Label>
                    {c.financial && !canViewFinancial ? (
                      <div className="h-9 flex items-center rounded-md border bg-slate-50 px-3 text-sm text-slate-400">$</div>
                    ) : c.type === "dropdown" ? (
                      <Select value={String(form.custom?.[c.key] ?? "")}
                        onValueChange={(v) => setCustomField(c.key, v)}>
                        <SelectTrigger data-testid={`detail-form-${c.key}`}><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          {(c.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : c.type === "boolean" ? (
                      <Select value={form.custom?.[c.key] == null ? "" : String(form.custom[c.key])}
                        onValueChange={(v) => setCustomField(c.key, v === "true")}>
                        <SelectTrigger data-testid={`detail-form-${c.key}`}><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true">Yes</SelectItem>
                          <SelectItem value="false">No</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        data-testid={`detail-form-${c.key}`}
                        type={RIGHT_TYPES.has(c.type) ? "number" : c.type === "date" ? "date" : "text"}
                        step={RIGHT_TYPES.has(c.type) ? "0.01" : undefined}
                        className={RIGHT_TYPES.has(c.type) ? "text-right tabular-nums" : ""}
                        value={form.custom?.[c.key] ?? ""}
                        onChange={(e) => setCustomField(c.key, e.target.value)}
                      />
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="detail-form-save-button" onClick={save} disabled={saving}>
              {saving ? "Saving..." : editing ? "Save Changes" : "Add Line"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk delete confirm */}
      <AlertDialog open={bulkDeleting} onOpenChange={(o) => !o && setBulkDeleting(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} line{selected.size !== 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {selected.size} selected revenue line{selected.size !== 1 ? "s" : ""} and update the {summaryTitle} immediately. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="revenue-details-bulk-delete-confirm-button"
              onClick={confirmBulkDelete} className="bg-red-600 hover:bg-red-700">
              Delete {selected.size} line{selected.size !== 1 ? "s" : ""}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete revenue line?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting && `${fmtMonth(deleting.reporting_month)} · ${deleting.client_name} · ${deleting.service} · ${fmtCurrencyMasked(deleting.invoice_amount)}`}
              <br />This will update the {summaryTitle} immediately. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="detail-delete-confirm-button" onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Single-cell edit dialog */}
      <Dialog open={!!cellEdit} onOpenChange={(o) => !o && setCellEdit(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit {cellEdit?.col.label || ""}</DialogTitle>
            <DialogDescription>
              {cellEdit && `${fmtMonth(cellEdit.item.reporting_month)} · ${cellEdit.item.client_name} · ${cellEdit.item.service}`}
            </DialogDescription>
          </DialogHeader>
          {cellEdit && (
            <div className="space-y-1.5">
              <Label>{cellEdit.col.label}</Label>
              {cellInput()}
              {cellEdit.col.key === "client_name" && (
                <p className="text-[11px] text-amber-700 dark:text-amber-400">
                  Renaming this client updates it everywhere in the application (all revenue lines and engagements).
                </p>
              )}
              {cellEdit.col.key === "invoice_amount" && (
                <p className="text-[11px] text-muted-foreground">
                  Setting this overrides the auto-calculated value (Pricing x Prorated FTE).
                </p>
              )}
              {(cellEdit.col.key === "pricing" || cellEdit.col.key === "prorated_ftes") && (
                <p className="text-[11px] text-muted-foreground">
                  If the invoice is auto-calculated, it will update automatically.
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCellEdit(null)}>Cancel</Button>
            <Button data-testid="rd-cell-edit-save" onClick={saveCell} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
